import React, {
  useCallback, useEffect, useRef, useState
} from 'react';

import BasicDropdown from '../../common/BasicDropdown/BasicDropdown';
import Label from '../../common/Labels/Label';
import SearchInput from '../../common/TextInputs/SearchInput/SearchInput';
import TimeRangePicker from '../../TimeRangePicker/TimeRangePicker';
import { RELATIVE_TIME_RANGES, TimeRangeType } from '../../../constants/time.constants';

const SORT_BY_OPTIONS = [
  { id: 'CreationDateDesc', label: 'Creation Date' },
  { id: 'LastRunDateDesc', label: 'Last Assay Run' },
]

enum DriftOption {
  AllStatus,
  Active,
  Paused,
  DriftDetected,
}

const DRIFT_DETECTED_OPTIONS = [
  { id: DriftOption.AllStatus, label: 'All Status' },
  { id: DriftOption.Active, label: 'Active' },
  { id: DriftOption.Paused, label: 'Paused' },
  { id: DriftOption.DriftDetected, label: 'Drift Detected' },
]

type AssayFilterControlsType = {
  onFilterControlChanged: Function,
}

type AssayFilterMetaType = {
  sortBy: string,
  name: string,
  active?: boolean,
  driftDetected?: string,
  start?: string,
  end?: string,
  timeRange?: object,
}

export const AssayFilterControls: React.FC<AssayFilterControlsType> = ({ onFilterControlChanged = () => {} }) => {
  const timer = useRef<any>(null);

  // We use two to distinguish between the displayed value and the debounced request
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filterSearchQuery, setFilterSearchQuery] = useState<string>('');

  const [statusDropdownValue, setStatusDropdownValue] = useState<number>(DriftOption.AllStatus);
  const [active, setActive] = useState<boolean | undefined>(undefined);
  const [driftDetected, setDriftDetected] = useState<string|undefined>(undefined);
  const [sortBy, setSortBy] = useState<string>(SORT_BY_OPTIONS[0].id);
  const [timeRange, setTimeRange] = useState<TimeRangeType>(RELATIVE_TIME_RANGES[7]);

  const showDateTimeDropdown = statusDropdownValue === DriftOption.DriftDetected;
  const statusDropdownLabel = statusDropdownValue === DriftOption.AllStatus ? 'All Assays' : statusDropdownValue === DriftOption.DriftDetected ? 'Drift Detected' : 'Active';

  const onInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    if (timer.current) {
      clearTimeout(timer.current)
    }
    timer.current = setTimeout(() => setFilterSearchQuery(e.target.value), 200)
  }, []);

  // Translates Status Dropdown value changes to filter values
  useEffect(() => {
    if (statusDropdownValue === DriftOption.AllStatus) {
      setActive(undefined);
      setDriftDetected(undefined);
    } else if (statusDropdownValue === DriftOption.DriftDetected) {
      setActive(undefined);
      setDriftDetected('Alert')
    } else if (statusDropdownValue === DriftOption.Paused){
      setActive(false);
      setDriftDetected(undefined);
    } else {
      setActive(true);
      setDriftDetected(undefined);
    }
  }, [statusDropdownValue])

  // When a value is changed, send the latest values through the provided callback
  useEffect(() => {
    const filterObj: AssayFilterMetaType = {
      name: filterSearchQuery,
      sortBy
    };

    if (showDateTimeDropdown) {
      filterObj.start = timeRange.fromDate;
      filterObj.end = timeRange.toDate;
      filterObj.timeRange = timeRange;
    }

    if (active !== undefined) {
      filterObj.active = active;
    }
    if (driftDetected !== undefined) {
      filterObj.driftDetected = driftDetected;
    }

    onFilterControlChanged(filterObj);
  }, [filterSearchQuery, driftDetected, sortBy, active, onFilterControlChanged, showDateTimeDropdown, timeRange])

  return (
    <div className="flex flex-col p-1">
      <div className="h-full w-full flex justify-between items-center p-1">
        <SearchInput
          className="relative border border-border-dark rounded mr-2 bg-white w-1/2"
          ariaLabel="Search by Assay Name"
          placeholder="Search by Assay Name"
          value={searchQuery}
          onChange={onInputChange}
        />
        <div className="flex items-start w-1/2">
          <BasicDropdown
            className="w-full"
            dropdownLabel=""
            buttonClass="items-start justify-start w-full"
            buttonLabelSize="md"
            values={DRIFT_DETECTED_OPTIONS}
            selectedValue={statusDropdownValue}
            onValueSelected={setStatusDropdownValue}
            hideLabelInSmallViewports={false}
          />
          <BasicDropdown
            dropdownLabel="Sort by"
            buttonLabelSize="md"
            values={SORT_BY_OPTIONS}
            selectedValue={sortBy}
            onValueSelected={setSortBy}
            hideLabelInSmallViewports={false}
          />
        </div>
      </div>
      {
        showDateTimeDropdown ? (
          <div className="flex items-center p-1 mt-1">
            <Label className="mr-1">{statusDropdownLabel}:</Label>
            <TimeRangePicker
              defaultRange={RELATIVE_TIME_RANGES[7] as any}
              selectedRange={timeRange}
              onChange={setTimeRange}
            />
          </div>
        ) : null
      }
    </div>
  )
}

export default AssayFilterControls;
